import type { FC } from "react";
import { useCallback } from "react";

import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuItem, SubMenu } from "mds/components/Menu";
import { AddPlusIcon, CopySendArrowIcon, CrossRemoveIcon, PersonGroupIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSectionId } from "store/selectors/base";
import {
  selectPageGroupIdsByPage,
  selectPageGroupUserIdFromSectionUser,
} from "store/selectors/page-groups";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";

interface StudentOptionsMenuProps {
  studentSectionUserId: string;
  isInGroup?: boolean;
  currentGroupId?: string;
  pageId: string;
  onRemove?: (studentSectionUserId: string) => void;
  isReadOnly?: boolean;
}

export const StudentOptionsMenu: FC<StudentOptionsMenuProps> = ({
  studentSectionUserId,
  isInGroup = false,
  currentGroupId,
  pageId,
  onRemove,
  isReadOnly = false,
}) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const pageGroupIds = useListSelector((state) => selectPageGroupIdsByPage(state, pageId));
  const pageGroupUserIdToUpdate = useAppSelector((state) =>
    selectPageGroupUserIdFromSectionUser(state, pageId, studentSectionUserId, currentGroupId),
  );
  const currentPageGroupUserId = currentGroupId !== undefined ? pageGroupUserIdToUpdate : null;

  const onRemoveFromGroup = async () => {
    if (!currentPageGroupUserId) {
      return;
    }
    try {
      await storeApi.page_group_users.destroy(currentPageGroupUserId);
      onRemove?.(studentSectionUserId);
    } catch (e) {
      toastLocalizedOperationCatcher("failed_to_remove_from_group")(e);
    }
  };

  const moveStudentToGroup = useCallback(
    (targetGroupId: string) => {
      if (!sectionId) {
        return;
      }

      const payload = {
        page_group_id: targetGroupId,
        section_user_id: studentSectionUserId,
      };

      if (pageGroupUserIdToUpdate) {
        return storeApi.page_group_users.partial_update({
          id: pageGroupUserIdToUpdate,
          ...payload,
        });
      }

      return storeApi.page_group_users.create({ section_id: sectionId, ...payload });
    },
    [sectionId, pageGroupUserIdToUpdate, studentSectionUserId],
  );

  const onMoveToGroup = async (targetGroupId: string) => {
    try {
      await moveStudentToGroup(targetGroupId);
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_move_to_group")(error);
    }
  };

  const onCreateNewGroup = async () => {
    if (!sectionId) {
      return;
    }
    try {
      const newGroup = await storeApi.page_groups.create({
        page_id: pageId,
        section_id: sectionId,
      });
      await moveStudentToGroup(newGroup.id);
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_create_new_group")(error);
    }
  };

  if (isReadOnly) {
    return null;
  }

  return (
    <SidebarOptionsMenu>
      {isInGroup && currentPageGroupUserId && (
        <MenuItem onClick={onRemoveFromGroup}>
          <IconText
            iconStart={<CrossRemoveIcon />}
            text={t("group_configuration_card.remove_from_group")}
          />
        </MenuItem>
      )}

      <SubMenu
        label={
          <IconText
            iconStart={<CopySendArrowIcon />}
            text={t("group_configuration_card.move_to_group")}
          />
        }
        overflow="auto"
      >
        {pageGroupIds.map((pageGroupId, index) => {
          const isCurrentGroup = pageGroupId === currentGroupId;
          const groupNumber = index + 1;

          return (
            <MenuItem
              disabled={isCurrentGroup}
              key={pageGroupId}
              onClick={() => onMoveToGroup(pageGroupId)}
            >
              <IconText
                iconStart={<PersonGroupIcon />}
                text={t("common.group_title", { number: groupNumber })}
              />
            </MenuItem>
          );
        })}

        <MenuItem onClick={onCreateNewGroup}>
          <IconText iconStart={<AddPlusIcon />} text={t("group_configuration_card.new_group")} />
        </MenuItem>
      </SubMenu>
    </SidebarOptionsMenu>
  );
};
